import shoppeHeaderImage from '@/../../public/images/shoppe/shoppe-header.webp';
import Banner from '@/components/Banner';
import ProductCard from '@/components/ProductCard';
import { Reveal } from '@/components/Reveal';
import ShareDialog from '@/components/ShareDialog';
import {
  Carousel,
  CarouselContent,
  CarouselControls,
  CarouselItem,
} from '@/components/ui/carousel';
import Typography from '@/components/ui/typography';
import Band from '@/Layouts/Band';
import SectionLayout from '@/Layouts/SectionLayout';
import { ProductVariant, ShoppeProduct } from '@/types';
import { Link } from '@inertiajs/react';

interface ShoppeGroup {
  name: string;
  slug: string;
  image: string;
}

type Variant = Pick<ProductVariant, 'color' | 'images'>;
type Product = Omit<ShoppeProduct<Variant>, 'code' | 'details'>;

interface Products {
  featured: Product[];
  new_arrival: Product[];
}

const CategoriesHeading = {
  featured: 'Featured',
  new_arrival: 'New arrivals',
};

const Index = ({
  groups,
  products,
}: {
  groups: ShoppeGroup[];
  products: Products;
}) => {
  return (
    <>
      <Banner
        image={shoppeHeaderImage}
        width={1600}
        height={900}
        alt="Shopping Cart"
        tagline="Discover, shop, enjoy"
        title="Shoppe Redefined"
        description="Fabric, aari frames, threads, tools and finished pieces — everything our classrooms use, shipped from our own stock."
      >
        <div className="flex w-full min-w-0 flex-col items-start gap-5">
          <Carousel className="w-full min-w-0">
            <CarouselContent className="-ml-3">
              {groups.map((group) => (
                <CarouselItem key={group.slug} className="basis-28 pl-3">
                  <Link
                    href={route('shoppe.showGroup', group.slug)}
                    className="group flex flex-col items-center gap-2"
                  >
                    <div className="aspect-square w-full overflow-hidden rounded-lg bg-ink-foreground/10">
                      <img
                        className="size-full object-cover transition-transform duration-500 group-hover:scale-105"
                        src={group.image}
                        alt=""
                        width={1280}
                        height={1280}
                        loading="lazy"
                      />
                    </div>
                    <Typography.Small className="text-center capitalize text-ink-foreground/80 transition-colors group-hover:text-primary">
                      {group.name}
                    </Typography.Small>
                  </Link>
                </CarouselItem>
              ))}
            </CarouselContent>
          </Carousel>

          <ShareDialog
            url={window.location.href}
            title={'Shoppe - ' + import.meta.env.VITE_APP_NAME}
            content="Discover, Shop, Enjoy"
          />
        </div>
      </Banner>

      {Object.keys(products).map((category, index) => (
        <Band
          key={category}
          tone={index % 2 ? 'surface' : 'raised'}
          seam={index > 0}
        >
          <SectionLayout width="wide" space="lg">
            <Reveal>
              {/* h2, not Display3's default h3: the Banner above carries the
                  page's h1 and nothing else sits between it and this section
                  heading. */}
              <Typography.Display3 as="h2">
                {CategoriesHeading[category as keyof Products]}
              </Typography.Display3>
            </Reveal>

            <Carousel className="flex w-full flex-col gap-6">
              <CarouselContent className="-ml-3">
                {products[category as keyof Products].map((product) => (
                  <CarouselItem
                    key={product.slug}
                    className="flex basis-1/2 pl-3 sm:basis-1/3 lg:basis-1/4 xl:basis-1/5"
                  >
                    <ProductCard product={product} />
                  </CarouselItem>
                ))}
              </CarouselContent>
              <CarouselControls />
            </Carousel>
          </SectionLayout>
        </Band>
      ))}
    </>
  );
};

export default Index;
